/**
 * 첫 이관 안내 팝업 — 온보딩(무료 서류 생성) 직후 사업장 홈에서 1회 노출.
 *
 * 사장님 지시(2026-08-19): 첫 이관은 이관 허브로 보내지 않고 팝업으로 "한번
 * 체험해 보는" 단계로 만든다. 신규 사용자가 처음 만나는 화면 중 하나 — 시스템의
 * 얼굴이므로 디자인·사용성에 공을 들인다.
 *
 *  · '지금 옮겨보기' → DocImportModal(기존 이관 모달)을 그 자리에서 연다.
 *  · '나중에 할게요' → intro_seen('first_import') 마킹 — 다시 뜨지 않고,
 *    이관은 사업장 홈의 '문서 업로드·분석'에 항상 있다는 안내 토스트만 남긴다.
 *  · 노출 여부 판단(1회·이관 이력 없음)은 부모(OnboardingFollowupGate)가 한다.
 */
import { FiArrowRight, FiCheckCircle, FiFileText, FiShield, FiUploadCloud } from 'react-icons/fi'
import Modal from '../../components/ui/Modal'

const POINTS = [
  {
    icon: <FiFileText size={17} />,
    title: '위험성평가 · JSA · 작업허가서 자동 인식',
    desc: 'KRAS 출력물, 엑셀, 한글(HWP), PDF — 표 안의 항목까지 읽어 하인리히 형식으로 정리합니다.',
  },
  {
    icon: <FiCheckCircle size={17} />,
    title: '반영 전, 눈으로 직접 확인',
    desc: 'AI가 읽은 내용을 검토 화면에서 항목별로 확인·선택한 뒤에만 저장됩니다. 마음대로 바꾸지 않아요.',
  },
  {
    icon: <FiShield size={17} />,
    title: '문서는 외부로 나가지 않습니다',
    desc: '다산 AI 자체 서버에서만 분석합니다. 올린 원본은 증빙용으로 안전하게 보관됩니다.',
  },
]

export default function FirstImportPopup({ open, onStart, onLater }: {
  open: boolean
  /** '지금 옮겨보기' — 부모가 DocImportModal 을 연다. */
  onStart: () => void
  /** '나중에 할게요' — 부모가 intro_seen 마킹 + 위치 안내. ESC·바깥 클릭도 동일. */
  onLater: () => void
}) {
  return (
    <Modal isOpen={open} onClose={onLater} hideCloseButton size="lg">
      {/* 히어로 — 브랜드 그라데이션. 모달 기본 패딩을 -m 으로 상쇄해 상단을 꽉 채운다. */}
      <div className="-m-6 mb-0 rounded-t-xl bg-gradient-to-br from-amber-500 via-orange-500 to-orange-600 px-7 pb-7 pt-8 text-white">
        <div className="mb-4 grid h-14 w-14 place-items-center rounded-2xl bg-white/15 shadow-inner backdrop-blur-sm">
          <FiUploadCloud size={28} />
        </div>
        <h2 className="text-[22px] font-extrabold leading-snug tracking-tight">
          쓰시던 안전 서류,<br />그대로 옮겨드릴게요
        </h2>
        <p className="mt-2 text-[13px] leading-relaxed text-white/85">
          새로 시작하실 필요 없어요 — 지금 갖고 계신 서류를 올리면
          방금 만든 서류들과 한 보드에서 함께 관리됩니다.
        </p>
        <span className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-white/20 px-3 py-1 text-[11.5px] font-bold">
          ✨ 무료 10건 — 지금 플랜 그대로
        </span>
      </div>

      {/* 3포인트 */}
      <div className="space-y-3 px-1 pb-1 pt-5">
        {POINTS.map((p) => (
          <div key={p.title} className="flex items-start gap-3 rounded-xl border border-surface-border bg-surface-elevated/25 px-3.5 py-3">
            <div className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-brand-primary/10 text-brand-primary">
              {p.icon}
            </div>
            <div className="min-w-0">
              <div className="text-[13px] font-bold text-ink">{p.title}</div>
              <p className="mt-0.5 text-[11.5px] leading-relaxed text-ink-muted">{p.desc}</p>
            </div>
          </div>
        ))}
      </div>

      {/* CTA */}
      <div className="flex items-center gap-2 pt-4">
        <button type="button" onClick={onLater}
          className="rounded-xl px-4 py-3 text-[13px] font-semibold text-ink-muted transition hover:bg-surface-elevated/40 hover:text-ink">
          나중에 할게요
        </button>
        <button type="button" onClick={onStart}
          className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 px-4 py-3 text-[14px] font-bold text-white shadow-md shadow-orange-500/25 transition hover:brightness-105 active:scale-[0.99]">
          지금 옮겨보기 <FiArrowRight size={16} />
        </button>
      </div>
      <p className="pt-2.5 text-center text-[10.5px] text-ink-faint">
        서류가 손에 없어도 괜찮아요 — 언제든 사업장 홈의 <b>문서 업로드·분석</b>에서 할 수 있습니다.
      </p>
    </Modal>
  )
}
